import { useEffect, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { api } from "@/helper/api";
import { getLmsDisplayText } from "@/components/lms/lms.utils";
import type { OverviewFilterKind } from "@/components/lms/lmsStatusOverviewScope";

export interface LmsStatusOverviewFilterOption {
  value: string;
  label: string;
}

interface CourseListItem {
  id: string | number;
  title?: unknown;
  name?: unknown;
}

export const toCourseFilterOptions = (
  courses: CourseListItem[],
): LmsStatusOverviewFilterOption[] =>
  courses
    .map((course) => ({
      value: String(course.id),
      label: getLmsDisplayText(course.title ?? course.name),
    }))
    .filter((option) => option.label);

interface Params {
  departmentCompanySubjectId?: string;
  filterKind: OverviewFilterKind;
  scopedRequestParameters: Record<string, string | number>;
  shouldNotImpersonate: boolean;
  suggestEnrolledCourses: boolean;
  t: TFunction;
}

export function useLmsStatusOverviewOptions({
  departmentCompanySubjectId,
  filterKind,
  scopedRequestParameters,
  shouldNotImpersonate,
  suggestEnrolledCourses,
  t,
}: Params) {
  const [departmentOptions, setDepartmentOptions] = useState<
    LmsStatusOverviewFilterOption[]
  >([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
  const [enrolledCourseOptions, setEnrolledCourseOptions] = useState<
    LmsStatusOverviewFilterOption[]
  >([]);
  const [isLoadingEnrolledCourses, setIsLoadingEnrolledCourses] =
    useState(false);

  const languageOptions = useMemo(
    () => [
      { value: "english", label: t("english") },
      { value: "deutsch", label: t("deutsch") },
    ],
    [t],
  );

  useEffect(() => {
    if (filterKind !== "department" || !departmentCompanySubjectId) {
      setDepartmentOptions([]);
      setIsLoadingDepartments(false);
      return;
    }

    let isMounted = true;
    setIsLoadingDepartments(true);

    api.genericApiRequest({
      entity: "department",
      method: "get",
      shouldNotImpersonate,
      getAllPages: true,
      parametersToRender: {
        depth: "0",
        company_subject_id__in: departmentCompanySubjectId,
      },
      successHandler: (response: any) => {
        if (!isMounted) {
          return;
        }

        const options = (response?.data?.results ?? []).map((department: any) => ({
          value: String(department.id),
          label: department.name,
        }));
        setDepartmentOptions(options);
        setIsLoadingDepartments(false);
      },
      failHandler: () => {
        if (!isMounted) {
          return;
        }
        setDepartmentOptions([]);
        setIsLoadingDepartments(false);
      },
    });

    return () => {
      isMounted = false;
    };
  }, [departmentCompanySubjectId, filterKind, shouldNotImpersonate]);

  useEffect(() => {
    if (filterKind !== "course" || !suggestEnrolledCourses) {
      setEnrolledCourseOptions([]);
      setIsLoadingEnrolledCourses(false);
      return;
    }

    let isMounted = true;
    setIsLoadingEnrolledCourses(true);

    api.genericApiRequest({
      entity: "course",
      method: "get",
      shouldNotImpersonate,
      getAllPages: true,
      parametersToRender: {
        ...scopedRequestParameters,
        has_company_enrolments: "true",
      },
      successHandler: (response: any) => {
        if (!isMounted) {
          return;
        }

        setEnrolledCourseOptions(
          toCourseFilterOptions(response?.data?.results ?? []).sort(
            (left, right) => left.label.localeCompare(right.label),
          ),
        );
        setIsLoadingEnrolledCourses(false);
      },
      failHandler: () => {
        if (!isMounted) {
          return;
        }
        setEnrolledCourseOptions([]);
        setIsLoadingEnrolledCourses(false);
      },
    });

    return () => {
      isMounted = false;
    };
  }, [
    filterKind,
    scopedRequestParameters,
    shouldNotImpersonate,
    suggestEnrolledCourses,
  ]);

  return {
    departmentOptions,
    enrolledCourseOptions,
    isLoadingDepartments,
    isLoadingEnrolledCourses,
    languageOptions,
  };
}
